import '../Main.scss';

import * as React from 'react';

import {
    Button,
    Col,
    Container,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row
} from 'reactstrap';

import { PortalTextCommon } from '../../Models/Api/strongbox.financialportal';

/**
 * ConfirmModalType basically determines what buttons will be in the footer
 * 
 * @member {yesno} - yes/no type modal.  There will be a yes and no button in the footer.  When the user
 * presses either, onTerminalButton will fire.  The yes parameter will be true if the user pressed yes.
 * The yes parameter will be explicitly no if the user presses no.
 * @member {yesnocancel} - yes/no type modal. Includes a cancel button. Works as yes no except that the 
 * yes parameter will be undefined if the user presses cancel.
 * @member {ok} - ok modal. There will be an OK button.  When the user presses it, onTerminalButton will fire 
 * with the yes value set to false.
 * 
 * */

export enum ConfirmModalType {
    yesno,
    ok,
    yesnocancel,
};

type Props = {
    msg: string | React.ReactElement;
    title?: string;
    onTerminalButton: (yes?: boolean) => void;
    modalType?: ConfirmModalType;
    commonText?: PortalTextCommon;
};

export const ConfirmModal: React.FC<Props> = (props): React.ReactElement => {
    const {
        modalType,
        onTerminalButton,
        commonText,
    } = props;

    const footerContent = React.useMemo(() => {
        if ((modalType === undefined) || (modalType === ConfirmModalType.yesno)) {
            return (
                <>
                    <Button color="secondary" onClick={() => onTerminalButton(false)}>{commonText?.no || ''}</Button>
                    <Button color="primary" onClick={() => onTerminalButton(true)}>{commonText?.yes || ''}</Button>
                </>
            );
        } else if (modalType === ConfirmModalType.yesnocancel) {
            return (
                <>
                    <Button color="secondary" onClick={() => onTerminalButton()}>{commonText?.cancel || ''}</Button>
                    <Button color="secondary" onClick={() => onTerminalButton(false)}>{commonText?.no || ''}</Button>
                    <Button color="primary" onClick={() => onTerminalButton(true)}>{commonText?.yes || ''}</Button>
                </>
            );
        } else if (modalType === ConfirmModalType.ok) {
            return (
                <>
                    <Button color="primary" onClick={() => onTerminalButton(false)}>{commonText?.okay || ''}</Button>
                </>
            )
        }
        // These are the only dependencies I actually want
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [modalType, onTerminalButton])

    return (
        <Modal
            className={`flight-modal`}
            isOpen={true}
            backdrop={'static'}
        >
            {
                props.title &&
                (
                    <ModalHeader toggle={() => props.onTerminalButton(false)}>{props.title}</ModalHeader>
                )
            }
            <ModalBody>
                <Container className={`control-region control-region-lender`}>
                    <Row>
                        <Col>
                            {props.msg}
                        </Col>
                    </Row>
                </Container>
            </ModalBody>
            <ModalFooter>
                {footerContent}
            </ModalFooter>
        </Modal>
    );
}
